import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../lib/firebase';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { supabase } from '../../lib/supabaseClient';
import imageCompression from 'browser-image-compression';
import logo from '../../assets/images/logo.png';
import { notificationService } from '../../services/notificationService';

interface Campus {
  id: number;
  name: string;
  city: string;
  full_address: string;
  university: { name: string; abbreviation: string };
  state: { name: string };
}

interface Category {
  id: number;
  name: string;
  slug: string;
  parent_id: number | null;
}

interface VendorBusiness {
  id: string;
  shopName: string;
  profileImage: string;
  coverImage?: string;
  isActive: boolean;
  userId: string;
  bio?: string;
  shopId?: string;
  contactPhone?: string;
  businessEmail?: string;
  location?: string;
  shippingPolicy?: string;
}

interface UserProfile {
  id: string;
  email: string;
  phone: string;
  name: string;
  role: string;
  isVerified: boolean;
  createdAt: string;
  profileImage?: string;
}

type StepKey =
  | 'shop'
  | 'title'
  | 'category'
  | 'description'
  | 'price'
  | 'inventory'
  | 'location'
  | 'images'
  | 'review';

interface StepConfig {
  key: StepKey;
  title: string;
  subtitle?: string;
  required: boolean;
}

// ========== NOTIFICATION ==========
const sendNewProductNotification = async (
  product: any,
  shopName: string,
  shopId: string
) => {
  try {
    const { data: users, error: usersError } = await supabase
      .from('users')
      .select('firebase_uid, email, name')
      .not('firebase_uid', 'is', null);

    if (usersError || !users || users.length === 0) return;

    const firebaseUids = users.map(u => u.firebase_uid).filter(Boolean);
    const emails = users.map(u => u.email).filter(Boolean);

    const formattedPrice = new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(product.price);

    const notificationTitle = `🆕 New Product Alert: ${product.title}`;
    const notificationBody = `${shopName} just launched "${product.title}" at ${formattedPrice}. Check it out now!`;

    const notificationData: any = {
      title: notificationTitle,
      body: notificationBody,
      notification_type: 'vendor',
      redirect_url: `/product/${product.id}`,
      data: {
        productId: product.id,
        productTitle: product.title,
        productPrice: product.price,
        shopId,
        shopName,
        type: 'new_product_launch',
        timestamp: new Date().toISOString()
      }
    };

    if (firebaseUids.length > 0) notificationData.target_user_ids = firebaseUids;
    if (emails.length > 0) notificationData.email_list = emails;

    const response = await notificationService.sendNotification(notificationData);

    if (response && response.success) {
      const inAppNotifications = users.map(user => ({
        user_id: user.firebase_uid,
        title: notificationTitle,
        message: notificationBody,
        type: 'new_product',
        data: {
          productId: product.id,
          productTitle: product.title,
          productPrice: product.price,
          shopId,
          shopName,
          redirect_url: `/product/${product.id}`
        },
        is_read: false,
        created_at: new Date().toISOString()
      }));

      const batchSize = 50;
      for (let i = 0; i < inAppNotifications.length; i += batchSize) {
        const batch = inAppNotifications.slice(i, i + batchSize);
        await supabase.from('user_notifications').insert(batch);
      }
    }
  } catch (error) {
    console.error('❌ sendNewProductNotification failed:', error);
  }
};

// Helper: coerce to null
const nn = (v: string | undefined | null): string | null => {
  const t = (v ?? '').trim();
  return t.length === 0 ? null : t;
};

// ========== COMPONENT ==========
const AddProductForm: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    inventory: '1'
  });
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedCampuses, setSelectedCampuses] = useState<Campus[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [campusSuggestions, setCampusSuggestions] = useState<Campus[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [productImages, setProductImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [imageDataUrls, setImageDataUrls] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [vendorBusinesses, setVendorBusinesses] = useState<VendorBusiness[]>([]);
  const [selectedShop, setSelectedShop] = useState<VendorBusiness | null>(null);
  const [allCampuses, setAllCampuses] = useState<Campus[]>([]);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [selectAllLocations, setSelectAllLocations] = useState(false);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showCongrats, setShowCongrats] = useState(false);
  const [createdProductId, setCreatedProductId] = useState('');

  const navigate = useNavigate();
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const locationDropdownRef = useRef<HTMLDivElement>(null);

  const steps: StepConfig[] = [
    { key: 'shop', title: 'Choose your shop', subtitle: 'Which shop are you listing this product under?', required: true },
    { key: 'title', title: 'What are you selling?', subtitle: 'A clear, searchable product or service name', required: true },
    { key: 'category', title: 'Pick a category', subtitle: 'Help buyers find your product', required: true },
    { key: 'description', title: 'Describe your product', subtitle: 'Features, condition, size, materials…', required: true },
    { key: 'price', title: 'Set your price', subtitle: 'How much does this product cost?', required: true },
    { key: 'inventory', title: 'How many do you have?', subtitle: 'Stock quantity available', required: true },
    { key: 'location', title: 'Delivery locations', subtitle: 'Where can buyers receive this product?', required: true },
    { key: 'images', title: 'Add product images', subtitle: 'Up to 7 high-quality images (at least 1 required)', required: true },
    { key: 'review', title: 'Review & launch', subtitle: 'Check your details before publishing', required: true }
  ];

  const currentStep = steps[currentStepIndex];
  const totalSteps = steps.length;
  const progressPercent = Math.round(((currentStepIndex + 1) / totalSteps) * 100);

  // ========== BOOTSTRAP: auth, shop, categories, campuses ==========
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await Promise.all([
          fetchUserData(user.uid),
          fetchVendorBusinesses(user.uid),
          fetchCategories(),
          fetchAllCampuses()
        ]);
      } else {
        setUserProfile(null);
        setVendorBusinesses([]);
        setSelectedShop(null);
      }
      setBootstrapping(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (vendorBusinesses.length > 0 && !selectedShop) {
      setSelectedShop(vendorBusinesses[0]);
    }
  }, [vendorBusinesses, selectedShop]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target as Node)) {
        setShowLocationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUserData = async (userId: string) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserProfile({
          id: userId,
          email: userData.email || '',
          phone: userData.phone || '',
          name: userData.name || 'User',
          role: userData.role || 'user',
          isVerified: userData.isVerified || false,
          createdAt: userData.createdAt?.toDate?.().toISOString() || new Date().toISOString(),
          profileImage: userData.profileImage || ''
        });
      } else {
        const user = auth.currentUser;
        if (user) {
          setUserProfile({
            id: user.uid,
            email: user.email || '',
            phone: user.phoneNumber || '',
            name: user.displayName || 'User',
            role: 'user',
            isVerified: user.emailVerified || false,
            createdAt: user.metadata.creationTime || new Date().toISOString(),
            profileImage: user.photoURL || ''
          });
        }
      }
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };

  const fetchVendorBusinesses = async (userId: string) => {
    try {
      const vendorsQuery = query(
        collection(db, 'vendors'),
        where('userId', '==', userId),
        where('isActive', '==', true)
      );
      const vendorsSnapshot = await getDocs(vendorsQuery);
      const businesses: VendorBusiness[] = vendorsSnapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          shopName: data.shopName || '',
          profileImage: data.profileImage || '',
          coverImage: data.coverImage || '',
          isActive: data.isActive || false,
          userId: data.userId || ''
        };
      });
      setVendorBusinesses(businesses);
    } catch (err) {
      console.error('Error fetching businesses:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error: cErr } = await supabase
        .from('categories')
        .select('*')
        .order('name');
      if (cErr) {
        console.error('Error fetching categories:', cErr);
        return;
      }
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchAllCampuses = async () => {
    try {
      const { data, error: cErr } = await supabase
        .from('campuses')
        .select(`
          id,
          name,
          city,
          full_address,
          universities!inner(name, abbreviation),
          states!inner(name)
        `)
        .order('universities(name)');

      if (cErr) {
        console.error('Error fetching campuses:', cErr);
        return;
      }

      const transformed: Campus[] = (data || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        city: c.city,
        full_address: c.full_address,
        university: {
          name: c.universities.name,
          abbreviation: c.universities.abbreviation
        },
        state: { name: c.states.name }
      }));
      setAllCampuses(transformed);
    } catch (err) {
      console.error('Error fetching campuses:', err);
    }
  };

  // ========== Location search ==========
  const searchCampuses = (query: string) => {
    const lowercaseQuery = query.toLowerCase().trim();
    if (lowercaseQuery === '') {
      const sorted = [...allCampuses].sort((a, b) =>
        a.university.name.localeCompare(b.university.name)
      );
      setCampusSuggestions(sorted.slice(0, 15));
      return;
    }
    const filtered = allCampuses.filter(c =>
      c.university.name.toLowerCase().includes(lowercaseQuery)
    );
    const sorted = filtered.sort((a, b) => {
      const aName = a.university.name.toLowerCase();
      const bName = b.university.name.toLowerCase();
      const aStarts = aName.startsWith(lowercaseQuery);
      const bStarts = bName.startsWith(lowercaseQuery);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return aName.localeCompare(bName);
    });
    setCampusSuggestions(sorted.slice(0, 12));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setSearchQuery(q);
    searchCampuses(q);
  };

  const handleSearchFocus = () => {
    if (searchQuery === '' && allCampuses.length > 0) {
      const sorted = [...allCampuses].sort((a, b) =>
        a.university.name.localeCompare(b.university.name)
      );
      setCampusSuggestions(sorted.slice(0, 15));
    }
  };

  const handleCampusSelect = (campus: Campus) => {
    const isSelected = selectedCampuses.find(c => c.id === campus.id);
    if (isSelected) {
      setSelectedCampuses(prev => prev.filter(c => c.id !== campus.id));
    } else {
      setSelectedCampuses(prev => [...prev, campus]);
    }
  };

  const handleSelectAllLocations = () => {
    if (window.confirm('Are you able to deliver to anywhere in Nigeria?')) {
      setSelectAllLocations(true);
      setSelectedCampuses([]);
      setShowLocationDropdown(false);
    }
  };

  const removeCampus = (campusId: number) => {
    setSelectedCampuses(prev => prev.filter(c => c.id !== campusId));
  };

  const handleCategorySelect = (categoryId: number) => {
    setSelectedCategory(categoryId);
    setShowCategoryDropdown(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ========== Images ==========
  const compressImage = async (file: File): Promise<File> => {
    const options = { maxSizeMB: 1, maxWidthOrHeight: 800, useWebWorker: true };
    return await imageCompression(file, options);
  };

  const fileToDataURL = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (productImages.length + files.length > 7) {
      setError('Maximum 7 images allowed');
      return;
    }
    for (const file of files) {
      try {
        const compressedFile = await compressImage(file);
        const previewUrl = URL.createObjectURL(compressedFile);
        const dataUrl = await fileToDataURL(compressedFile);
        setProductImages(prev => [...prev, compressedFile]);
        setImagePreviews(prev => [...prev, previewUrl]);
        setImageDataUrls(prev => [...prev, dataUrl]);
      } catch (err) {
        console.error('Image processing error:', err);
        setError('Failed to process image. Please try another.');
      }
    }
  };

  const removeImage = (index: number) => {
    URL.revokeObjectURL(imagePreviews[index]);
    setProductImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setImageDataUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length > 0) {
      const dt = new DataTransfer();
      imageFiles.forEach(f => dt.items.add(f));
      handleImageSelect({ target: { files: dt.files } } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  // ========== Step validation ==========
  const isStepValid = (key: StepKey): boolean => {
    switch (key) {
      case 'shop': return !!selectedShop;
      case 'title': return formData.title.trim().length >= 2;
      case 'category': return !!selectedCategory;
      case 'description': return formData.description.trim().length >= 5;
      case 'price': return !!formData.price && parseFloat(formData.price) > 0;
      case 'inventory': return !!formData.inventory && parseInt(formData.inventory) >= 1;
      case 'location': return selectAllLocations || selectedCampuses.length > 0;
      case 'images': return productImages.length > 0;
      case 'review': return true;
      default: return true;
    }
  };

  const canProceed = isStepValid(currentStep.key);

  const goNext = () => {
    if (!canProceed) return;
    setError('');
    setCurrentStepIndex(i => Math.min(i + 1, totalSteps - 1));
  };

  const goBack = () => {
    setError('');
    setCurrentStepIndex(i => Math.max(i - 1, 0));
  };

  // ========== Submit ==========
  const handleSubmit = async () => {
    if (loading) return;
    setError('');
    setLoading(true);

    try {
      if (!currentUser) throw new Error('You must be logged in to add products.');
      if (!selectedShop) throw new Error('Please select a shop to add products to.');
      if (formData.title.trim().length < 2) throw new Error('Product title is required');
      if (!formData.price || parseFloat(formData.price) <= 0) throw new Error('Valid price is required');
      if (!selectedCategory) throw new Error('Please select a category');
      if (productImages.length === 0) throw new Error('At least one product image is required');
      if (selectedCampuses.length === 0 && !selectAllLocations) throw new Error('Please add at least one delivery location');

      const shopId = selectedShop.id;

      // Ensure vendor profile exists
      const { data: existingVendorProfile, error: vpcErr } = await supabase
        .from('vendor_profiles')
        .select('id, vendor_id, shop_name, total_products')
        .eq('user_id', currentUser.uid)
        .eq('vendor_id', shopId)
        .maybeSingle();

      if (vpcErr) console.warn('Vendor profile check warning:', vpcErr.message);

      let vendorProfileId: string | undefined;
      let currentVendorProductCount = 0;

      if (!existingVendorProfile) {
        const { data: newVendorProfile, error: createErr } = await supabase
          .from('vendor_profiles')
          .insert([{
            user_id: currentUser.uid,
            vendor_id: shopId,
            shop_name: selectedShop.shopName,
            profile_image: selectedShop.profileImage || '',
            cover_image: selectedShop.coverImage || '',
            bio: selectedShop.bio || '',
            is_active: true,
            onboarding_completed: true,
            total_products: 0
          }])
          .select()
          .single();

        if (createErr) {
          console.error('Vendor profile create error:', createErr);
        } else if (newVendorProfile) {
          vendorProfileId = newVendorProfile.id;
          currentVendorProductCount = newVendorProfile.total_products || 0;
        }
      } else {
        vendorProfileId = existingVendorProfile.id;
        currentVendorProductCount = existingVendorProfile.total_products || 0;
      }

      // Unique slug
      const baseSlug = formData.title
        .toLowerCase()
        .replace(/[^a-z0-9 -]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .substring(0, 90);

      let finalSlug = baseSlug;
      let slugCounter = 1;
      while (slugCounter < 10) {
        const { data: existingSlug } = await supabase
          .from('products')
          .select('slug')
          .eq('slug', finalSlug)
          .maybeSingle();
        if (!existingSlug) break;
        finalSlug = `${baseSlug}-${slugCounter}`;
        slugCounter++;
      }
      if (slugCounter >= 10) finalSlug = `${baseSlug}-${Date.now()}`;

      // All product columns written
      const productData = {
        vendor_id: shopId,
        user_id: currentUser.uid,
        title: formData.title.trim(),
        slug: finalSlug,
        description: nn(formData.description),
        price: Math.round(parseFloat(formData.price)),
        currency: 'NGN',
        images: imageDataUrls,
        variations: null,
        inventory: parseInt(formData.inventory) || 1,
        is_active: true,
        category: categories.find(c => c.id === selectedCategory)?.name || null,
        condition: 'new',
        tags: [],
        views_count: 0,
        likes_count: 0,
        sales_count: 0,
        is_promoted: false,
        promotion_ends_at: null,
        vendor_logo: selectedShop.profileImage || null,
        vendor_name: selectedShop.shopName,
        cart_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: product, error: productError } = await supabase
        .from('products')
        .insert([productData])
        .select()
        .single();

      if (productError) throw new Error(`Failed to create product: ${productError.message}`);
      if (!product) throw new Error('Failed to create product: No data returned');

      // Product -> category
      const { error: catErr } = await supabase
        .from('product_categories')
        .insert([{ product_id: product.id, category_id: selectedCategory }]);
      if (catErr) console.warn('Category link failed:', catErr.message);

      // Product -> delivery locations
      if (selectedCampuses.length > 0) {
        const deliveryLocations = selectedCampuses.map(campus => ({
          product_id: product.id,
          campus_id: campus.id
        }));
        const { error: locErr } = await supabase
          .from('product_delivery_locations')
          .insert(deliveryLocations);
        if (locErr) console.warn('Delivery location link failed:', locErr.message);
      }

      // Vendor profile product count + category + delivery locations
      if (vendorProfileId) {
        await supabase
          .from('vendor_profiles')
          .update({
            total_products: currentVendorProductCount + 1,
            updated_at: new Date().toISOString()
          })
          .eq('id', vendorProfileId);

        await supabase
          .from('vendor_categories')
          .upsert([{ vendor_id: vendorProfileId, category_id: selectedCategory }], {
            onConflict: 'vendor_id,category_id',
            ignoreDuplicates: true
          });

        if (selectedCampuses.length > 0) {
          const vendorLocations = selectedCampuses.map(campus => ({
            vendor_id: vendorProfileId,
            campus_id: campus.id,
            delivery_fee: 0,
            delivery_radius_m: null
          }));
          await supabase
            .from('vendor_delivery_locations')
            .upsert(vendorLocations, {
              onConflict: 'vendor_id,campus_id',
              ignoreDuplicates: true
            });
        }
      }

      sendNewProductNotification(product, selectedShop.shopName, selectedShop.id);

      setCreatedProductId(product.id);
      setShowCongrats(true);
    } catch (err: any) {
      console.error('❌ Product creation error:', err);
      setError(err?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ========== BOOTSTRAP LOADER ==========
  if (bootstrapping) {
    return (
      <div className="min-h-screen bg-[#F4F1E8] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B4819] mx-auto" />
          <p className="mt-4 text-gray-600 text-sm">Setting up your listing studio…</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-[#F4F1E8] flex flex-col justify-center py-12 px-4">
        <div className="max-w-md mx-auto text-center">
          <img src={logo} alt="Campus GOSTOREz" className="h-24 w-auto mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-[#9B4819] mb-4">Authentication Required</h2>
          <p className="text-gray-600 mb-6">Please log in to add products or service to your shop.</p>
          <button
            onClick={() => navigate('/signin')}
            className="w-full py-3 bg-[#9B4819] text-white rounded-xl font-semibold hover:bg-[#7a3914] transition"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  if (vendorBusinesses.length === 0) {
    return (
      <div className="min-h-screen bg-[#F4F1E8] flex flex-col justify-center py-12 px-4">
        <div className="max-w-md mx-auto text-center">
          <img src={logo} alt="Campus GOSTOREz" className="h-24 w-auto mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-[#9B4819] mb-4">Online Shop Required</h2>
          <p className="text-gray-600 mb-6">You need to create an online shop before adding products or services.</p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/vendor-onboarding')}
              className="w-full py-3 bg-[#9B4819] text-white rounded-xl font-semibold hover:bg-[#7a3914] transition"
            >
              Create Your Online Shop
            </button>
            <button
              onClick={() => navigate('/vendor/dashboard')}
              className="w-full py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ========== CONGRATULATIONS ==========
  if (showCongrats) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F4F1E8] to-[#efe9d8] flex items-center justify-center px-4 py-10">
        <style>{`
          @keyframes gz-pop { 0% { transform: scale(0.6); opacity: 0; } 55% { transform: scale(1.08); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
          @keyframes gz-fade-up { 0% { transform: translateY(14px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
          @keyframes gz-ring { 0% { transform: scale(0.9); opacity: 0.65; } 100% { transform: scale(1.6); opacity: 0; } }
          .gz-pop { animation: gz-pop 0.6s cubic-bezier(0.2, 0.9, 0.3, 1.2) both; }
          .gz-fade-1 { animation: gz-fade-up 0.6s ease-out 0.25s both; }
          .gz-fade-2 { animation: gz-fade-up 0.6s ease-out 0.45s both; }
          .gz-ring { animation: gz-ring 1.4s ease-out infinite; }
        `}</style>

        <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-8 sm:p-10 border border-black/5 text-center">
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className="absolute inset-0 rounded-full bg-emerald-200/70 gz-ring" />
            <div className="absolute inset-0 rounded-full bg-emerald-500 gz-pop flex items-center justify-center shadow-lg">
              <svg viewBox="0 0 24 24" className="w-12 h-12 text-white" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 gz-fade-1">
            Product live 🎉
          </h1>
          <p className="mt-2 text-sm text-gray-600 gz-fade-1">
            <span className="font-semibold text-[#9B4819]">{formData.title}</span> is now available in {selectedShop?.shopName}.
          </p>

          <div className="mt-8 space-y-3 gz-fade-2">
            <button
              onClick={() => navigate(`/vendor/${selectedShop?.id}`)}
              className="w-full py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-[#9B4819] hover:bg-[#7a3914] transition shadow-sm"
            >
              View in Shop
            </button>
            <button
              onClick={() => navigate('/vendor/dashboard')}
              className="w-full py-3.5 px-4 rounded-xl text-sm font-semibold text-[#9B4819] bg-amber-50 hover:bg-amber-100 transition border border-amber-200"
            >
              View in Studio
            </button>
            <button
              onClick={() => navigate('/market')}
              className="w-full py-3.5 px-4 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition"
            >
              Search in Marketplace
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-3.5 px-4 rounded-xl text-sm font-semibold text-[#9B4819] bg-white border border-[#9B4819]/30 hover:bg-[#FDF8F3] transition"
            >
              Post More Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ========== MAIN WIZARD ==========
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F4F1E8] to-[#efe9d8] py-8 sm:py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-center mb-4 relative">
          {onClose && (
            <button
              onClick={onClose}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow hover:bg-gray-50"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <img src={logo} alt="Campus GOSTOREz" className="h-16 w-auto" />
        </div>

        <div className="text-center mb-6 px-2">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#9B4819] leading-tight">
            Upload a product or service
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 max-w-md mx-auto">
            Reach <span className="font-semibold text-[#9B4819]">100k+ registered potential customers</span> in minutes.
          </p>
        </div>

        <div className="bg-white/80 backdrop-blur rounded-2xl border border-black/5 shadow-sm p-5">
          <div className="flex items-center justify-between text-xs font-medium text-gray-600">
            <span>Step {currentStepIndex + 1} of {totalSteps}</span>
            <span>{progressPercent}%</span>
          </div>
          <div className="mt-3 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="h-2 rounded-full bg-gradient-to-r from-[#9B4819] to-[#c56b3a] transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="mt-6 bg-white rounded-3xl shadow-xl border border-black/5 p-6 sm:p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{currentStep.title}</h2>
            {currentStep.subtitle && (
              <p className="mt-1.5 text-sm text-gray-500">{currentStep.subtitle}</p>
            )}
          </div>

          {/* STEP: shop */}
          {currentStep.key === 'shop' && (
            <div className="space-y-3">
              {vendorBusinesses.map(shop => (
                <button
                  key={shop.id}
                  type="button"
                  onClick={() => setSelectedShop(shop)}
                  className={`w-full flex items-center gap-3 p-3 rounded-2xl border transition ${
                    selectedShop?.id === shop.id
                      ? 'border-[#9B4819] bg-[#FDF8F3]'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  {shop.profileImage ? (
                    <img src={shop.profileImage} alt={shop.shopName} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#9B4819] text-white flex items-center justify-center font-semibold">
                      {shop.shopName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 text-left">
                    <p className="font-semibold text-gray-900">{shop.shopName}</p>
                    <p className="text-xs text-gray-500">Active shop</p>
                  </div>
                  {selectedShop?.id === shop.id && (
                    <svg className="w-5 h-5 text-[#9B4819]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* STEP: title */}
          {currentStep.key === 'title' && (
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g. Vintage Denim Jacket"
              autoFocus
              maxLength={200}
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base"
            />
          )}

          {/* STEP: category */}
          {currentStep.key === 'category' && (
            <div ref={categoryDropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className={`w-full px-4 py-3.5 rounded-xl border text-left transition ${
                  selectedCategory
                    ? 'border-[#9B4819] bg-[#FDF8F3] text-[#9B4819]'
                    : 'border-gray-200 bg-white text-gray-700'
                }`}
              >
                <span className="flex items-center justify-between">
                  {selectedCategory
                    ? categories.find(c => c.id === selectedCategory)?.name
                    : 'Select a category'}
                  <svg
                    className={`w-5 h-5 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </button>

              {showCategoryDropdown && (
                <div className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-y-auto">
                  {categories.map(category => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleCategorySelect(category.id)}
                      className={`w-full p-3 text-left text-sm transition ${
                        selectedCategory === category.id
                          ? 'bg-[#9B4819] text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP: description */}
          {currentStep.key === 'description' && (
            <textarea
              name="description"
              rows={5}
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe features, condition, size, materials…"
              autoFocus
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base resize-none"
            />
          )}

          {/* STEP: price */}
          {currentStep.key === 'price' && (
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">₦</span>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="0"
                min="1"
                step="1"
                autoFocus
                className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base"
              />
            </div>
          )}

          {/* STEP: inventory */}
          {currentStep.key === 'inventory' && (
            <input
              type="number"
              name="inventory"
              value={formData.inventory}
              onChange={handleChange}
              placeholder="1"
              min="1"
              autoFocus
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base"
            />
          )}

          {/* STEP: location */}
          {currentStep.key === 'location' && (
            <div ref={locationDropdownRef}>
              <div className="mb-3">
                {selectAllLocations ? (
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-200">
                    <span className="font-semibold text-green-800">All Nigeria (nationwide)</span>
                    <button
                      type="button"
                      onClick={() => setSelectAllLocations(false)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : selectedCampuses.length > 0 ? (
                  <div className="space-y-2">
                    {selectedCampuses.map(campus => (
                      <div key={campus.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-200">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 text-sm truncate">
                            {campus.university.name} — {campus.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {campus.city}, {campus.state.name}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeCampus(campus.id)}
                          className="ml-3 text-red-500 hover:text-red-700 p-1"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm text-center">No locations selected yet.</p>
                )}
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowLocationDropdown(!showLocationDropdown)}
                  className="w-full px-4 py-3 bg-[#9B4819] text-white rounded-xl font-semibold hover:bg-[#7a3914] transition"
                >
                  {selectAllLocations ? 'Change Delivery Locations' : 'Select Delivery Locations'}
                </button>

                {showLocationDropdown && (
                  <div className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-96 overflow-y-auto">
                    <div className="p-4">
                      <div className="flex justify-between items-center mb-3">
                      
                        <button
                          type="button"
                          onClick={() => setShowLocationDropdown(false)}
                          className="text-gray-500 hover:text-gray-700 p-1"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

                      <input
                        type="text"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onFocus={handleSearchFocus}
                        placeholder="Search institutions around your delivery locations"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-sm mb-3"
                      />

                      <div className="space-y-2 max-h-56 overflow-y-auto">
                        {campusSuggestions.map(campus => {
                          const checked = !!selectedCampuses.find(c => c.id === campus.id);
                          return (
                            <label
                              key={campus.id}
                              className={`flex items-center p-3 rounded-lg cursor-pointer transition ${
                                checked ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => handleCampusSelect(campus)}
                                className="w-4 h-4 text-[#9B4819] rounded border-gray-300 focus:ring-[#9B4819]"
                              />
                              <div className="ml-3 flex-1 min-w-0">
                                <p className="font-medium text-gray-900 text-sm truncate">
                                  {campus.university.name} — {campus.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {campus.university.abbreviation}, {campus.city}, {campus.state.name}
                                </p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP: images */}
          {currentStep.key === 'images' && (
            <div>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageSelect}
                disabled={productImages.length >= 7 || loading}
                className="hidden"
                id="product-images"
              />
              <label
                htmlFor="product-images"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`block border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition ${
                  productImages.length >= 7
                    ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                    : 'border-gray-300 bg-gray-50 hover:border-[#9B4819] hover:bg-[#FDF8F3]'
                }`}
              >
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" />
                </svg>
                <p className="font-semibold text-gray-700 mb-1">
                  {productImages.length >= 7 ? 'Maximum images reached' : 'Tap to upload or drag & drop'}
                </p>
                <p className="text-xs text-gray-500">PNG, JPG • Max 1MB each</p>
              </label>

              {imagePreviews.length > 0 && (
                <div className="mt-5">
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    {productImages.length}/7 uploaded
                  </p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <div className="aspect-square rounded-xl overflow-hidden border border-gray-200">
                          <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-full object-cover" />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          disabled={loading}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-lg hover:bg-red-600 opacity-0 group-hover:opacity-100 transition"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP: review */}
          {currentStep.key === 'review' && (
            <div className="space-y-3 text-left">
              <ReviewRow label="Shop" value={selectedShop?.shopName || '—'} />
              <ReviewRow label="Title" value={formData.title} />
              <ReviewRow label="Category" value={categories.find(c => c.id === selectedCategory)?.name || '—'} />
              <ReviewRow label="Description" value={formData.description} />
              <ReviewRow label="Price" value={`₦${parseInt(formData.price || '0').toLocaleString()}`} />
              <ReviewRow label="Inventory" value={formData.inventory} />
              <ReviewRow
                label="Locations"
                value={selectAllLocations ? 'All Nigeria' : selectedCampuses.map(c => c.university.name).join(', ')}
              />
              <ReviewRow label="Images" value={`${productImages.length} uploaded`} />
            </div>
          )}

          {error && (
            <div className="mt-5 rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-8 flex items-center gap-3">
            <button
              type="button"
              onClick={goBack}
              disabled={currentStepIndex === 0 || loading}
              className="flex-1 py-3.5 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Back
            </button>

            {currentStep.key !== 'review' ? (
              <button
                type="button"
                onClick={goNext}
                disabled={!canProceed || loading}
                className="flex-1 py-3.5 rounded-xl text-sm font-semibold text-white bg-[#9B4819] hover:bg-[#7a3914] transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 py-3.5 rounded-xl text-sm font-semibold text-white bg-[#9B4819] hover:bg-[#7a3914] transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Publishing…
                  </>
                ) : (
                  'Launch Product'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ReviewRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-start gap-3 border-b border-gray-100 pb-2">
    <span className="text-xs font-medium text-gray-500 w-24 shrink-0 pt-0.5">{label}</span>
    <span className="text-sm text-gray-800 break-words flex-1">{value}</span>
  </div>
);

export default AddProductForm;